import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Modal,
    Paper,
    Grid,
    FormControl,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    FilledInput,
    InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Formik, useFormik, useField, useFormikContext } from 'formik';
import * as Yup from 'yup';
import axios from 'utils/axios';
import { SET_MODAL_STATUS, SET_TOAST_OBJECT, SET_LOADING } from 'store/actions';
import FormFields from 'Components/FormFields';
import AttachmentOutlinedIcon from '@mui/icons-material/AttachmentOutlined';
import { checkUploadedFile, UploadFile } from 'utils/functions';
import { uploadFeebackAttachment, submitFeedback } from 'utils/apolloQueries';
function FeedbackModal() {
    const theme = useTheme();
    const inputRef = useRef();
    const { companyData, userData } = useSelector((state) => state.customization);
    const [tempFile, setTempFile] = useState();
    const [selectedFile, setSelectedFile] = useState(null);
    const [formData, setFormData] = useState({ title: null, description: null });
    const dispatch = useDispatch();
    const handleClose = () => {
        dispatch({ type: SET_MODAL_STATUS, modalStatus: false });
        setSelectedFile(null);
    };
    const handleFileChange = (event) => {
        if (event.target.files) {
            let file = event.target.files[0];
            const data = checkUploadedFile(file);
            if (data && data.toastObject) {
                dispatch({ type: SET_TOAST_OBJECT, toastObject: data });
            } else {
                dispatch({ type: SET_TOAST_OBJECT, toastObject: { status: false } });
                setSelectedFile(file);
            }
        } else {
            setSelectedFile(null);
        }
    };
    return (
        <>
            <Box className="modal-header">
                <h3>Give feedback</h3>
            </Box>
            <Box className="modal-body">
                <Formik
                    initialValues={{
                        title: formData.title || '',
                        description: formData.description || '',
                        attachment: selectedFile ? selectedFile.name : '',
                        file: ''
                    }}
                    validationSchema={Yup.object().shape({
                        title: Yup.string().required('Reason is required'),
                        description: Yup.string().required('Description is required')
                        // attachment: Yup.string().required('Attachment is required')
                    })}
                    enableReinitialize
                    onSubmit={async (values, { setErrors, setStatus, setSubmitting, resetForm, setFieldValue }) => {
                        dispatch({ type: SET_LOADING, loading: true });
                        let variables = null;
                        if (selectedFile) {
                            const uploadFile = await UploadFile(selectedFile, uploadFeebackAttachment);
                            if (uploadFile.uploadFeebackAttachment.key) {
                                variables = {
                                    feedbackInput: {
                                        attachment: uploadFile.uploadFeebackAttachment.key,
                                        companyId: companyData._id,
                                        description: values.description,
                                        title: values.title
                                    }
                                };
                            }
                        } else {
                            variables = {
                                feedbackInput: {
                                    attachment: null,
                                    companyId: companyData._id,
                                    description: values.description,
                                    title: values.title
                                }
                            };
                        }
                        if (variables) {
                            let submitData = { query: submitFeedback, variables: variables };
                            axios({
                                method: 'POST',
                                data: submitData
                            })
                                .then(function (response) {
                                    dispatch({ type: SET_LOADING, loading: false });
                                    if (response.status === 200) {
                                        // const { submitFeedback } = response.data.data;
                                        dispatch({
                                            type: SET_TOAST_OBJECT,
                                            toastObject: {
                                                status: true,
                                                variant: 'success',
                                                message: 'Feedback has been submitted'
                                            }
                                        });
                                        dispatch({ type: SET_MODAL_STATUS, modalStatus: false });
                                        setSelectedFile(null);
                                    } else {
                                        dispatch({ type: SET_LOADING, loading: false });
                                        dispatch({
                                            type: SET_TOAST_OBJECT,
                                            toastObject: {
                                                status: true,
                                                variant: 'warning',
                                                message: 'Something went wrong. Please try again.'
                                            }
                                        });
                                    }
                                    if (response?.data?.errors[0]?.message === 'Bad Request Exception') {
                                        dispatch({
                                            type: SET_TOAST_OBJECT,
                                            toastObject: {
                                                status: true,
                                                variant: 'error',
                                                message: response.data.errors[0].extensions.response.message[0]
                                            }
                                        });
                                    }
                                })
                                .catch(function (response) {
                                    dispatch({ type: SET_LOADING, loading: false });
                                    // dispatch({
                                    //     type: SET_TOAST_OBJECT,
                                    //     toastObject: {
                                    //         status: true,
                                    //         variant: 'error',
                                    //         message: 'Something went wrong. Please try again.'
                                    //     }
                                    // });
                                });
                        } else {
                            dispatch({
                                type: SET_TOAST_OBJECT,
                                toastObject: {
                                    status: true,
                                    variant: 'error',
                                    message: 'Something went wrong. Please try again.'
                                }
                            });
                        }
                    }}
                >
                    {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                        <form noValidate id="feedback-form">
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <FormControl error={Boolean(touched.title && errors.title)} sx={{ ...theme.typography.customInput }}>
                                        <FormFields
                                            type="outlined-input"
                                            name="title"
                                            label="Reason for feedback"
                                            onChange={(e) => {
                                                setFormData({ ...formData, title: e.target.value });
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl
                                        error={Boolean(touched.description && errors.description)}
                                        sx={{ ...theme.typography.customInput }}
                                    >
                                        <FormFields
                                            type="outlined-input"
                                            name="description"
                                            label="Description"
                                            onChange={(e) => {
                                                setFormData({ ...formData, description: e.target.value });
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl className="attachment-holder">
                                        <InputLabel htmlFor={`filled-adornment-attachment`}>Attachment</InputLabel>
                                        <OutlinedInput
                                            id="filled-adornment-attachment"
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <AttachmentOutlinedIcon />
                                                </InputAdornment>
                                            }
                                            onClick={() => inputRef.current.click()}
                                            fullWidth
                                            value={selectedFile && selectedFile.name ? selectedFile.name : ''}
                                            readOnly
                                            className="MuiInputLabel-shrink"
                                        />
                                        <input type="file" name="attachment" ref={inputRef} hidden onChange={(e) => handleFileChange(e)} />
                                        {touched.attachment && errors.attachment && (
                                            <FormHelperText error id="standard-weight-helper-text-attachment-login">
                                                {errors.attachment}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Grid container spacing={0}>
                                <Grid item xs={12} className="modal-footer">
                                    <Button
                                        onClick={handleClose}
                                        size="small"
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        className="alternate-reversed"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSubmit}
                                        size="small"
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        className="alternate"
                                    >
                                        Send
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>
            </Box>
        </>
    );
}

export default FeedbackModal;
